import React from "react";
import { useApp } from "../context/AppContext";
import { AlertTriangle, Phone, Heart, ShieldCheck } from "lucide-react";

const CONTENT = {
  hu: {
    kicker: "18+ · Felelős Játék",
    title: "Játssz felelősen",
    intro: "A Havana Casino elkötelezett a felelős szórakoztatás mellett. Bár Play Money módon működünk (a zsetonok nem válthatók készpénzre), a szerencsejátékok szimulációja is függőségi mintákat okozhat. Ez az oldal segít téged, hogy a játék szórakozás maradjon.",
    warnings_h: "Mikor kérj segítséget?",
    warnings: [
      "Ha többet költesz zsetonokra, mint amit meg tudsz engedni magadnak",
      "Ha munkádat, tanulmányodat vagy kapcsolatodat elhanyagolod a játék miatt",
      "Ha nyugtalan vagy ingerült vagy, amikor nem játszol",
      "Ha veszteségek után \"csak még egyszer\"-t játszol",
      "Ha titkolod a játékodat a családod elől",
      "Ha kölcsönt vagy hitelt veszel fel zseton vásárlásra",
    ],
    tools_h: "Eszközök a kontroll megtartására",
    tools: [
      { t: "Napi limit", d: "Állíts be maximum napi költési limitet a Cashier oldalon (Beállítások → Limits)." },
      { t: "Önkizárás", d: "24 órás / 30 napos / 6 hónapos / állandó önkizárás — email küldve support@havanacasino.hu címre." },
      { t: "Session időmérő", d: "Kapsz értesítést minden 30 perc után, hogy tudd, mennyit játszottál." },
      { t: "Fiók törlés", d: "Bármikor véglegesen törölheted a fiókodat: privacy@havanacasino.hu." },
    ],
    help_h: "Ingyenes segélyvonalak",
    help: [
      { name: "Nemzeti Szerencsejáték-függőség Segélyvonal", phone: "+36 80 15 15 15", when: "24/7 · anonim · ingyenes" },
      { name: "Kék Pont Alapítvány", phone: "+36 1 215 7833", when: "H-P 9:00-17:00" },
      { name: "Játékos-Segítő", phone: "www.jatekossegito.hu", when: "Online chat és fórum" },
    ],
    close: "Emlékezz: A Havana Casino kizárólag szórakoztatásra szolgál. Ha úgy érzed, hogy már nem élvezed, tarts szünetet — vagy iratkozz ki. Az egészséged és a családod fontosabb.",
  },
  en: {
    kicker: "18+ · Responsible Gambling",
    title: "Play Responsibly",
    intro: "Havana Casino is committed to responsible entertainment. Although we operate in Play Money mode (chips cannot be redeemed for cash), simulated gambling can still trigger addictive patterns. This page helps you keep gaming as fun.",
    warnings_h: "When to seek help",
    warnings: [
      "Spending more on chips than you can comfortably afford",
      "Neglecting work, study or relationships due to gaming",
      "Feeling restless or irritable when not playing",
      "Chasing losses with \"just one more\" spins",
      "Hiding gaming from your family",
      "Borrowing money to buy chips",
    ],
    tools_h: "Tools to stay in control",
    tools: [
      { t: "Daily limit", d: "Set a max daily spend on Cashier → Settings → Limits." },
      { t: "Self-exclusion", d: "24h / 30d / 6mo / permanent — email support@havanacasino.hu." },
      { t: "Session timer", d: "Get notified every 30 minutes of playtime." },
      { t: "Account deletion", d: "Delete your account any time: privacy@havanacasino.hu." },
    ],
    help_h: "Free helplines",
    help: [
      { name: "National Problem Gambling Helpline (HU)", phone: "+36 80 15 15 15", when: "24/7 · anonymous · free" },
      { name: "GamCare (UK)", phone: "0808 8020 133", when: "24/7 · confidential" },
      { name: "BeGambleAware (Intl)", phone: "www.begambleaware.org", when: "Online chat & resources" },
    ],
    close: "Remember: Havana Casino is entertainment only. If it stops being fun, take a break — or opt out. Your health and family matter more.",
  },
};

export default function ResponsibleGambling() {
  const { lang } = useApp();
  const c = CONTENT[lang] || CONTENT.en;
  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="text-xs uppercase tracking-[0.4em] text-red-400 mb-2 flex items-center gap-2">
        <AlertTriangle size={14}/> {c.kicker}
      </div>
      <h1 className="font-serif text-4xl mb-4">{c.title}</h1>
      <p className="text-white/70 mb-8 leading-relaxed">{c.intro}</p>

      <div className="lux-card p-6 mb-6 bg-yellow-950/20 border-yellow-500/20">
        <h2 className="font-serif text-xl mb-3 flex items-center gap-2 text-yellow-400"><AlertTriangle size={20}/>{c.warnings_h}</h2>
        <ul className="space-y-2 text-sm text-white/70">
          {c.warnings.map((w, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-yellow-500 mt-0.5">•</span><span>{w}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="lux-card p-6 mb-6">
        <h2 className="font-serif text-xl mb-4 flex items-center gap-2 text-[#d4af37]"><ShieldCheck size={20}/>{c.tools_h}</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {c.tools.map((t, i) => (
            <div key={i} className="rounded bg-black/40 border border-white/10 p-4">
              <div className="font-bold text-sm">{t.t}</div>
              <div className="text-xs text-white/60 mt-1 leading-relaxed">{t.d}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="lux-card p-6 mb-6 bg-emerald-950/20 border-emerald-500/20">
        <h2 className="font-serif text-xl mb-4 flex items-center gap-2 text-emerald-400"><Phone size={20}/>{c.help_h}</h2>
        <div className="space-y-3">
          {c.help.map((h, i) => (
            <div key={i} className="rounded bg-black/40 border border-white/10 p-4">
              <div className="font-bold">{h.name}</div>
              <div className="text-[#d4af37] font-mono text-sm mt-1">{h.phone}</div>
              <div className="text-xs text-white/50 mt-1">{h.when}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="text-center text-sm text-white/60 italic border-t border-white/10 pt-6 flex items-start gap-2">
        <Heart className="text-red-400 shrink-0 mt-0.5" size={16}/>
        <span>{c.close}</span>
      </div>
    </div>
  );
}
